import { Box, Divider, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { CarrierType, CarrierVisitDirection } from '@planning/app/api'
import { GateInGeneralCargoOrderListItem } from '@planning/pages/GateControl/Components/GateInGeneralCargoOrderListItem'
import { GateInOrderListItem } from '@planning/pages/GateControl/Components/GateInOrderListItem'
import { CreateGeneralCargoOrder } from '@planning/pages/Order/CreateGeneralCargoOrders'
import { CreateOrderModal } from '@planning/pages/Order/CreateOrderModal'
import { orderService } from '@planning/services'
import { ValidateOutboundOrderFunc } from '@planning/stores/gateControl/ValidateOutboundDto'
import { NonNumericOrderWithPickUpAmount } from '@planning/stores/truckAppointment/TruckAppointmentDetailsViewStore'
import { useTranslate } from '@tolgee/react'
import {
  ButtonOption,
  ButtonWithDropdown,
  IconButton,
  PlusIcon,
  useMinimalsTheme,
} from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useState } from 'react'
import { IInspectContainerFormData } from './InspectContainer'
import { NNROrderListItem } from './NNROrderListItem'
import { OrderListItem } from './OrderListItem'

interface Props {
  orders: IInspectContainerFormData[]
  bookings?: IInspectContainerFormData[]
  dropOffGeneralCargoOrders?: IInspectContainerFormData[]
  pickUpGeneralCargoOrders?: IInspectContainerFormData[]
  nnrOrders?: NonNumericOrderWithPickUpAmount[]
  type: 'PickUp' | 'DropOff'
  onAddOrder?: (order?: IInspectContainerFormData) => void
  onEditOrder?: (order: IInspectContainerFormData) => void
  onRemoveOrder?: (order: IInspectContainerFormData) => void
  onRemoveGeneralCargoOrder?: (order: IInspectContainerFormData) => void
  onRemoveNNROrder?: (order: NonNumericOrderWithPickUpAmount) => void
  onEditNnrOrderAmount?: (nnrOrder: NonNumericOrderWithPickUpAmount) => void
  useSearchAutoComplete?: boolean
  renderAutocomplete?: () => JSX.Element
  error: boolean
  isAllowedToCreateOrders?: boolean
  validateOutboundRequest?: ValidateOutboundOrderFunc
  orderIdsWithNoAllocationSpace?: number[]
  isContainerCheckRequired?: boolean
  isForAppointment?: boolean
  hideHoldEnforcement?: boolean
}

export const OrderHandlingBox: FC<Props> = observer(
  ({
    orders,
    bookings,
    dropOffGeneralCargoOrders,
    pickUpGeneralCargoOrders,
    nnrOrders,
    type,
    onAddOrder,
    onEditOrder,
    onRemoveOrder,
    onEditNnrOrderAmount,
    onRemoveNNROrder,
    onRemoveGeneralCargoOrder,
    useSearchAutoComplete,
    renderAutocomplete,
    error,
    isAllowedToCreateOrders,
    validateOutboundRequest,
    isContainerCheckRequired,
    isForAppointment,
    hideHoldEnforcement,
  }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { gateInViewStore, dialogStore, appViewStore } = usePlanningStore()
    const pickUpSearchType = gateInViewStore.pickUpOrderSearchStore.searchType
    const dropOffSearchType = gateInViewStore.dropOffOrderSearchStore.searchType

    const titleMap = {
      PickUp: t('pickUps', 'Pick ups'),
      DropOff: t('dropOffs', 'Drop offs'),
    }
    const emptyListLabelMap = {
      PickUp: t('exitsEmpty', 'Exits empty'),
      DropOff: t('entersEmpty', 'Enters empty'),
    }

    const [isAddingOrder, setIsAddingOrder] = useState<boolean>(false)

    const toggleShowAutocomplete = () => {
      setIsAddingOrder(!isAddingOrder)
    }

    const getCreateOrderOptions = (): ButtonOption[] => {
      const forcedDirection =
        type === 'DropOff' ? CarrierVisitDirection.Inbound : CarrierVisitDirection.Outbound

      const onAddUnitOrder = () => {
        dialogStore.openDialog(
          <CreateOrderModal
            onSubmit={createdOrders => {
              if (!createdOrders) {
                appViewStore.setShowAlert(
                  'error',
                  t(
                    'failedToAssignOrderPleaseDoItManually',
                    'Failed to assign order. Please do it manually',
                  ),
                )
                return
              }

              if (forcedDirection === CarrierVisitDirection.Inbound) {
                const orderId = createdOrders[CarrierVisitDirection.Inbound]
                gateInViewStore.upsertDropoffOrderById(orderId)
              } else {
                const orderId = createdOrders[CarrierVisitDirection.Outbound]
                gateInViewStore.upsertPickUpOrderById(orderId)
              }
            }}
            onCancel={() => dialogStore.close()}
            inboundCarrierType={
              forcedDirection === CarrierVisitDirection.Inbound ? CarrierType.Truck : undefined
            }
            outboundCarrierType={
              forcedDirection === CarrierVisitDirection.Outbound ? CarrierType.Truck : undefined
            }
            isoCodeMappingFieldRenderFn={gateInViewStore.isoCodeMappingFieldRenderFn}
          />,
          true,
        )
      }

      const onAddGeneralCargoOrder = () => {
        dialogStore.openDialog(
          <CreateGeneralCargoOrder
            isTruckAppointment
            forcedDirection={forcedDirection}
            onCreated={async orderId => {
              const order = await orderService.getById(orderId)
              if (order) {
                gateInViewStore.upsertOrder(order as IInspectContainerFormData, 'generalCargo')
              }
            }}
          />,
          true,
        )
      }

      const options: ButtonOption[] = [
        {
          label: t('unit', 'Unit'),
          testId: 'create-unit-order-btn',
          onClick: onAddUnitOrder,
        },
        {
          label: t('generalCargo', 'General cargo'),
          testId: 'create-general-cargo-order-btn',
          onClick: onAddGeneralCargoOrder,
        },
      ]

      return options
    }

    const EmptyList = () => (
      <Typography
        color={!error ? theme.palette.grey[500] : theme.palette.error.main}
        textAlign={'center'}
      >
        {emptyListLabelMap[type]}
      </Typography>
    )

    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: !error ? 'rgba(145, 158, 171, 0.32)' : theme.palette.error.main,
          borderRadius: theme.customRadius.medium,
          bgcolor: 'white',
          mb: '1rem !important',
        }}
      >
        <Box
          sx={{
            padding: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='subtitle2'>{titleMap[type]}</Typography>
          <Box display='flex' alignItems='center'>
            {useSearchAutoComplete && isAddingOrder && isAllowedToCreateOrders && (
              <ButtonWithDropdown
                label={t('createOrder', 'Create order')}
                color='inherit'
                options={getCreateOrderOptions()}
              />
            )}

            {(useSearchAutoComplete || onAddOrder) && (
              <IconButton
                data-cy={`order-handling-add-${type}-order-btn`}
                onClick={() => (useSearchAutoComplete ? toggleShowAutocomplete() : onAddOrder?.())}
              >
                <PlusIcon
                  sx={{
                    transform: useSearchAutoComplete && isAddingOrder ? 'rotate(45deg)' : '0',
                    transition: 'transform .2s',
                  }}
                />
              </IconButton>
            )}
          </Box>
        </Box>

        {useSearchAutoComplete && isAddingOrder && renderAutocomplete && (
          <Box
            sx={{ pr: theme.customSpacing.m, pl: theme.customSpacing.m, pb: theme.customSpacing.m }}
          >
            {renderAutocomplete()}
          </Box>
        )}

        <Divider sx={{ borderColor: !error ? '' : theme.palette.error.main }} />
        <Box
          sx={{
            padding: '1rem',
            bgcolor: !error ? theme.palette.grey[100] : theme.palette.error.lighter,
            borderBottomLeftRadius: theme.customRadius.medium,
            borderBottomRightRadius: theme.customRadius.medium,
          }}
        >
          {type === 'PickUp' &&
            useSearchAutoComplete &&
            pickUpSearchType === 'generalCargo' &&
            pickUpGeneralCargoOrders &&
            pickUpGeneralCargoOrders.length > 0 &&
            pickUpGeneralCargoOrders?.map(o => (
              <GateInGeneralCargoOrderListItem
                key={`gate-in-order-list-item-${o.id}`}
                order={o}
                onRemove={onRemoveGeneralCargoOrder}
                validateOutboundRequest={validateOutboundRequest}
              />
            ))}

          {type === 'DropOff' &&
            useSearchAutoComplete &&
            dropOffSearchType === 'generalCargo' &&
            dropOffGeneralCargoOrders &&
            dropOffGeneralCargoOrders.length > 0 &&
            dropOffGeneralCargoOrders?.map(o => (
              <GateInGeneralCargoOrderListItem
                key={`gate-in-order-list-item-${o.id}`}
                order={o}
                onRemove={onRemoveGeneralCargoOrder}
              />
            ))}

          {/* NNR Orders */}
          {type === 'PickUp' &&
            pickUpSearchType !== 'generalCargo' &&
            nnrOrders!.length > 0 &&
            nnrOrders?.map(nnr => (
              <NNROrderListItem
                key={`nnr-order-list-item-${nnr.id}`}
                nnrOrder={nnr}
                arrivalDate={gateInViewStore.truckAppointmentDate}
                isForAppointment={isForAppointment}
                onRemove={onRemoveNNROrder}
                showThreeDotMenu={useSearchAutoComplete}
                onEditPickUpAmount={onEditNnrOrderAmount}
              />
            ))}

          {useSearchAutoComplete &&
            type === 'DropOff' &&
            dropOffSearchType !== 'generalCargo' &&
            orders!.length > 0 &&
            orders.map(o => (
              <GateInOrderListItem
                key={`gate-in-order-list-item-${o.id}`}
                order={o}
                onEdit={onEditOrder}
                onRemove={onRemoveOrder}
                hideHoldEnforcement={hideHoldEnforcement}
              />
            ))}

          {useSearchAutoComplete &&
            type === 'DropOff' &&
            dropOffSearchType !== 'generalCargo' &&
            bookings &&
            bookings.length > 0 &&
            bookings.map(o => (
              <GateInOrderListItem
                key={`gate-in-order-list-item-${o.id}`}
                order={o}
                onRemove={onRemoveOrder}
              />
            ))}

          {useSearchAutoComplete &&
            type === 'PickUp' &&
            pickUpSearchType !== 'generalCargo' &&
            orders!.length > 0 &&
            orders.map(o => (
              <GateInOrderListItem
                key={`gate-in-order-list-item-${o.id}`}
                order={o}
                onRemove={onRemoveOrder}
                isPickUp
                hideHoldEnforcement={hideHoldEnforcement}
              />
            ))}

          {!useSearchAutoComplete
            ? orders.map(o =>
                !o.containerNumber && !!o.commodityId ? (
                  <GateInGeneralCargoOrderListItem
                    key={`gate-in-order-list-item-${o.id}`}
                    order={o}
                    onRemove={onRemoveOrder}
                    validateOutboundRequest={validateOutboundRequest}
                  />
                ) : (
                  <OrderListItem
                    key={`order-list-item-${o.id}`}
                    order={o}
                    onEdit={onEditOrder}
                    onRemove={onRemoveOrder}
                    isContainerCheckRequired={isContainerCheckRequired}
                    hideHoldEnforcement={hideHoldEnforcement}
                  />
                ),
              )
            : ''}

          {/* Empty List */}
          {!orders.length &&
            !bookings?.length &&
            !nnrOrders?.length &&
            !pickUpGeneralCargoOrders?.length &&
            !dropOffGeneralCargoOrders?.length && <EmptyList />}
        </Box>
      </Box>
    )
  },
)
